import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppNavItem } from '../../services/menu-mapping';
import { NavigationService } from '../../services/navigation.service';
import { AuthService } from '../../services/auth.service';
import { ThemeService } from '../../services/theme.service';
import { SidebarPinnedService } from './sidebar-pinned.service';
import { SidebarRecentsService } from './sidebar-recents.service';

/**
 * Command Palette — overlay global ⌘K / Ctrl+K para "jump to anything".
 *
 * Patron inspirado en Linear, Vercel, Notion, Raycast. El usuario presiona
 * ⌘K (o Ctrl+K en Windows), aparece un overlay con search prominente y una
 * lista de resultados agrupados:
 *   1. Recientes       — ultimas paginas visitadas (boost de score si la query es corta)
 *   2. Paginas         — todos los modulos del menu
 *   3. Acciones        — comandos rapidos: cambiar tema, densidad, idioma, cerrar sesion
 *
 * Por que es un service en lugar de viver dentro del sidebar:
 *   - El overlay tiene que poder cubrir TODA la app (incluido el sidebar y
 *     el header). Si vive dentro del sidebar, el z-index se vuelve fragil.
 *   - El listener global de keydown (⌘K) tiene que activarse desde cualquier
 *     pagina, no solo cuando el sidebar esta montado.
 *   - El estado (open/close, query, selected) se comparte entre el listener
 *     global y el componente que renderiza la UI.
 *
 * Donde se monta:
 *   - El componente CommandPaletteComponent va en `app.component.html` (root).
 *   - El listener de keydown se registra en este service en su constructor.
 *
 * Performance:
 *   - El score se calcula on-the-fly en `results` (computed signal). Sin la
 *     palette abierta el computed no se evalua. Con la palette abierta y
 *     n=200 items la search es trivial (lineal con scoring simple, <1ms).
 *   - No fuzzy completo (Fuse.js, etc.) — substring matching alcanza.
 */

export type CommandKind = 'page' | 'recent' | 'action';

export interface CommandItem {
  /** Identificador estable para trackBy. */
  id: string;
  /** Categoria del item — controla el grupo donde aparece y el orden. */
  kind: CommandKind;
  /** Label visible. */
  label: string;
  /** Subtitle / breadcrumb opcional (ej. nombre del grupo padre). */
  hint?: string;
  /** Nombre de icono legacy ('cart', 'group', ...) que `<app-icon>` resuelve. */
  icon?: string;
  /** Accion al ejecutar: navegar a path o ejecutar callback. Exactamente uno. */
  path?: string;
  run?: () => void;
  /** Score relativo a la query — mayor = mas relevante. */
  score?: number;
}

/** Maximo de resultados por grupo, para no inundar la lista. */
const MAX_PER_GROUP = 8;

@Injectable({ providedIn: 'root' })
export class CommandPaletteService {
  private router = inject(Router);
  private nav = inject(NavigationService);
  private auth = inject(AuthService);
  private theme = inject(ThemeService);
  private pinned = inject(SidebarPinnedService);
  private recents = inject(SidebarRecentsService);
  private translate = inject(TranslateService);

  // ====== Estado expuesto al componente ======
  readonly isOpen = signal(false);
  readonly query = signal('');
  readonly selectedIdx = signal(0);

  // ====== Items "fuente" (no filtrados) ======
  /** Pages: todos los modulos hoja del menu actual. Se recomputa cuando cambia
   *  el menu (login, cambio de permisos, cambio de idioma). */
  private readonly allPages = computed<CommandItem[]>(() => {
    const items = this.nav.currentItems;
    return this.flattenLeaves(items);
  });

  /** Acciones fijas: cambiar tema, densidad, idioma, logout, etc. Sus labels
   *  se resuelven al momento del computed (sigue cambios de idioma). */
  private readonly allActions = computed<CommandItem[]>(() => {
    // Re-leemos translate al invalidar — esto se recomputa al cambiar de idioma
    // porque `query` (otro signal) cambia o porque alguien fuerza una invalidacion.
    // Para asegurar reactividad robusta, el componente que renderiza ya escucha
    // `onLangChange` y abre/cierra el palette para forzar refresh si fuera necesario.
    const t = (k: string) => this.translate.instant(k);
    return [
      {
        id: 'action:toggle-theme',
        kind: 'action' as const,
        label: t('commandPalette.acciones.cambiarTema'),
        hint: this.theme.currentTheme === 'dark' ? t('commandPalette.actual.oscuro') : t('commandPalette.actual.claro'),
        icon: this.theme.currentTheme === 'dark' ? 'sun' : 'moon',
        run: () => this.theme.switchTheme(),
      },
      {
        id: 'action:toggle-density',
        kind: 'action' as const,
        label: t('commandPalette.acciones.cambiarDensidad'),
        hint: this.theme.currentDensity === 'compact' ? t('commandPalette.actual.compacto') : t('commandPalette.actual.normal'),
        icon: 'sparkles',
        run: () => this.theme.toggleDensity(),
      },
      {
        id: 'action:go-profile',
        kind: 'action' as const,
        label: t('commandPalette.acciones.miPerfil'),
        icon: 'user',
        path: '/user-profile',
      },
      {
        id: 'action:open-chat',
        kind: 'action' as const,
        label: t('commandPalette.acciones.abrirChat'),
        icon: 'message',
        path: '/chat',
      },
      {
        id: 'action:logout',
        kind: 'action' as const,
        label: t('commandPalette.acciones.cerrarSesion'),
        icon: 'logout',
        run: () => this.auth.logOut(),
      },
    ];
  });

  /** Recientes derivados del RecentsService. */
  private readonly allRecents = computed<CommandItem[]>(() => {
    return this.recents.items().map((r, i) => ({
      id: `recent:${r.path}`,
      kind: 'recent' as const,
      label: r.label,
      icon: r.icon,
      path: r.path,
      // Score base para que aparezcan al tope sin query (i=0 mas reciente).
      score: 1000 - i,
    }));
  });

  /**
   * Resultados filtrados y ordenados por la query actual. Si la query esta
   * vacia, devolvemos:
   *   - Recientes (top — son lo mas probable que el user quiere)
   *   - Acciones
   *   - Una muestra de paginas (no todas — el menu puede tener 50+)
   *
   * Si hay query, scoreamos contra todos los items y devolvemos los top-N
   * por kind.
   */
  readonly results = computed<{ recents: CommandItem[]; pages: CommandItem[]; actions: CommandItem[]; total: number }>(() => {
    const q = this.query().trim().toLowerCase();
    const recents = this.allRecents();
    const pages = this.allPages();
    const actions = this.allActions();

    if (!q) {
      const trimmedRecents = recents.slice(0, MAX_PER_GROUP);
      const trimmedActions = actions.slice(0, MAX_PER_GROUP);
      const trimmedPages = pages.slice(0, MAX_PER_GROUP);
      return {
        recents: trimmedRecents,
        actions: trimmedActions,
        pages: trimmedPages,
        total: trimmedRecents.length + trimmedActions.length + trimmedPages.length,
      };
    }

    const scoredRecents = this.scoreItems(recents, q).slice(0, MAX_PER_GROUP);
    const scoredPages = this.scoreItems(pages, q).slice(0, MAX_PER_GROUP);
    const scoredActions = this.scoreItems(actions, q).slice(0, MAX_PER_GROUP);

    return {
      recents: scoredRecents,
      pages: scoredPages,
      actions: scoredActions,
      total: scoredRecents.length + scoredPages.length + scoredActions.length,
    };
  });

  /** Lista plana (recents + pages + actions) en el orden que aparece en el UI —
   *  usado para keyboard nav (Up/Down indices). */
  readonly flatResults = computed<CommandItem[]>(() => {
    const r = this.results();
    return [...r.recents, ...r.pages, ...r.actions];
  });

  constructor() {
    // Listener global de teclado. Atajos:
    //   ⌘K / Ctrl+K        → abrir palette
    //   Escape (si abierto) → cerrar palette
    //
    // Otros keys (↑↓ Enter) los maneja el componente cuando esta visible —
    // aqui solo el toggle global para que funcione desde cualquier pagina.
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', (e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
          // Evitar pisar el Ctrl+K de "Limpiar consola" en DevTools — solo si
          // el target NO es un input dentro de un editor que usa Ctrl+K (raro).
          e.preventDefault();
          this.toggle();
        } else if (e.key === 'Escape' && this.isOpen()) {
          e.preventDefault();
          this.close();
        }
      });
    }
  }

  open(): void {
    this.query.set('');
    this.selectedIdx.set(0);
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  toggle(): void {
    this.isOpen() ? this.close() : this.open();
  }

  setQuery(q: string): void {
    this.query.set(q);
    this.selectedIdx.set(0); // Al tipear, reset del selected al primero.
  }

  moveSelection(delta: number): void {
    const max = this.flatResults().length - 1;
    if (max < 0) return;
    const next = this.selectedIdx() + delta;
    // Wrap-around — al pasar del ultimo va al primero y viceversa (UX Linear).
    if (next < 0) this.selectedIdx.set(max);
    else if (next > max) this.selectedIdx.set(0);
    else this.selectedIdx.set(next);
  }

  /** Ejecuta el item seleccionado por teclado (Enter) o por click. */
  execute(item?: CommandItem): void {
    const target = item ?? this.flatResults()[this.selectedIdx()];
    if (!target) return;
    this.close();
    if (target.run) target.run();
    else if (target.path) this.router.navigateByUrl(target.path);
  }

  // ====== Helpers privados ======

  /** Aplana el arbol del menu y devuelve solo las hojas (items con path). */
  private flattenLeaves(items: AppNavItem[] | undefined, parentLabel?: string, acc: CommandItem[] = []): CommandItem[] {
    if (!items) return acc;
    for (const it of items) {
      if (it.items?.length) {
        // Grupo: bajamos pasando su label como hint para los descendientes.
        this.flattenLeaves(it.items, it.text, acc);
      } else if (it.path) {
        acc.push({
          id: `page:${it.key ?? it.path}`,
          kind: 'page',
          label: it.text,
          hint: parentLabel,
          icon: it.icon,
          path: it.path,
        });
      }
    }
    return acc;
  }

  /**
   * Score por substring. Simple y rapido — bumps por:
   *   - +200 si label empieza con la query
   *   - +120 si contiene la query
   *   - +30  si el hint contiene la query
   * Devolvemos solo items con score > 0, ordenados desc.
   */
  private scoreItems(items: CommandItem[], q: string): CommandItem[] {
    const scored: CommandItem[] = [];
    for (const it of items) {
      const label = it.label.toLowerCase();
      const hint = (it.hint ?? '').toLowerCase();
      let score = it.score ?? 0;
      if (label.startsWith(q)) score += 200;
      else if (label.includes(q)) score += 120;
      if (hint.includes(q)) score += 30;
      if (score > 0) scored.push({ ...it, score });
    }
    scored.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    return scored;
  }
}
